import { Row, Col } from "antd";
import { RightOutlined } from "@ant-design/icons";
import { COMMON_DICTS, getMetadataLabel, type CustomerDTO } from "@ifa/utils";
import { deriveAge } from "../../helpers";
import dayjs from "dayjs";
import CustomButton from "@/components/CustomButton";
import CustomCard from "@/components/CustomCard/CustomCard";
import styles from "../index.module.css";

// 导入图标
import fnaIcon from "@/assets/img/FNA.png";
import ownerIcon from "@/assets/img/owner.png";
import insuredIcon from "@/assets/img/insured.png";
import beneficiaryIcon from "@/assets/img/beneficiaryForm.png";
import historyIcon from "@/assets/img/history.png";

interface CustomerDetailViewProps {
  customer: CustomerDTO;
  onNewOrder: (customerId: number, e: React.MouseEvent) => void;
  onEditCustomer: () => void;
  onNavigateToSection: (section: string) => void;
}

export function CustomerDetailView({ customer, onNewOrder, onEditCustomer, onNavigateToSection }: CustomerDetailViewProps) {
  return (
    <div className={styles.customerDetailView}>
      {/* 客户基本信息 CustomCard */}
      <CustomCard className={styles.basicInfoCard} bodyStyle={{ padding: 0 }}>
        {/* 自定义标题区域 */}
        <div className={styles.cardCustomHeader}>
          <div className={styles.cardTitleRow}>
            <div className={styles.customerNames}>
              <span className={styles.detailChineseName}>{customer.chineseName || "-"}</span>
              <span className={styles.detailEnglishName}>{customer.englishName || "-"}</span>
            </div>
            <div className={styles.detailActions}>
              <CustomButton height={36} fontSize={14} bgColor="#fff" color="#666" style={{ border: "1px solid #d9d9d9", padding: "0 16px" }} onClick={onEditCustomer}>
                修改資料
              </CustomButton>
              <CustomButton height={36} fontSize={14} bgColor="#00C7B6" color="#fff" style={{ padding: "0 16px" }} onClick={(e: React.MouseEvent) => onNewOrder(customer.customerId!, e)}>
                新建訂單
              </CustomButton>
            </div>
          </div>
        </div>

        {/* 客户信息网格 */}
        <div className={styles.infoGridContainer}>
          <Row gutter={[24, 16]}>
            <Col span={12}>
              <div className={styles.infoItem}>
                <div className={styles.infoLabel}>{getMetadataLabel("ID_TYPE", customer.idType, COMMON_DICTS.idType)}</div>
                <div className={styles.infoValue}>{customer.idNumber || "-"}</div>
              </div>
            </Col>
            <Col span={12}>
              <div className={styles.infoItem}>
                <div className={styles.infoLabel}>手提電話</div>
                <div className={styles.infoValue}>{customer.policyholder?.personal?.phone || "-"}</div>
              </div>
            </Col>
            <Col span={12}>
              <div className={styles.infoItem}>
                <div className={styles.infoLabel}>出生日期</div>
                <div className={styles.infoValue}>{customer.birthDate ? `${dayjs(customer.birthDate).format("YYYY-MM-DD")} (${deriveAge(customer) || "--"}歲)` : "-"}</div>
              </div>
            </Col>
            <Col span={12}>
              <div className={styles.infoItem}>
                <div className={styles.infoLabel}>顧問</div>
                <div className={styles.infoValue}>
                  {customer.advisorName}
                  {customer.iaCode && ` (${customer.iaCode})`}
                </div>
              </div>
            </Col>
          </Row>
        </div>

        {/* 更新时间 - 单独一行,背景色 */}
        <div className={styles.updateTimeRow}>
          <span className={styles.updateTimeLabel}>更新時間</span>
          <span className={styles.updateTimeValue}>{customer.updateTime ? dayjs(customer.updateTime).format("YYYY-MM-DD HH:mm") : "-"}</span>
        </div>
      </CustomCard>

      {/* 账户信息 CustomCard */}
      <CustomCard title="賬號資料" className={styles.accountInfoCard} style={{ marginTop: "16px" }}>
        <div className={styles.statsSection}>
          <div className={styles.statCard} onClick={() => onNavigateToSection("fna")}>
            <div className={styles.statIcon}>
              <img src={fnaIcon} alt="FNA" />
            </div>
            <div className={styles.statContent}>
              <div className={styles.statLabel}>FNA</div>
            </div>
            <div className={styles.statRight}>
              <div className={styles.statValue}>{customer.fnaCount || 0}</div>
              <div className={styles.statArrow}>
                <RightOutlined />
              </div>
            </div>
          </div>
          <div className={styles.statCard} onClick={() => onNavigateToSection("profile")}>
            <div className={styles.statIcon}>
              <img src={ownerIcon} alt="客戶投保人" />
            </div>
            <div className={styles.statContent}>
              <div className={styles.statLabel}>客戶投保人</div>
            </div>
            <div className={styles.statRight}>
              <div className={styles.statValue}>{customer.policyholderCount || 0}</div>
              <div className={styles.statArrow}>
                <RightOutlined />
              </div>
            </div>
          </div>
          <div className={styles.statCard} onClick={() => onNavigateToSection("insured")}>
            <div className={styles.statIcon}>
              <img src={insuredIcon} alt="受保人" />
            </div>
            <div className={styles.statContent}>
              <div className={styles.statLabel}>受保人</div>
            </div>
            <div className={styles.statRight}>
              <div className={styles.statValue}>{customer.insuredCount || 0}</div>
              <div className={styles.statArrow}>
                <RightOutlined />
              </div>
            </div>
          </div>
          <div className={styles.statCard} onClick={() => onNavigateToSection("beneficiary")}>
            <div className={styles.statIcon}>
              <img src={beneficiaryIcon} alt="受益人" />
            </div>
            <div className={styles.statContent}>
              <div className={styles.statLabel}>受益人</div>
            </div>
            <div className={styles.statRight}>
              <div className={styles.statValue}>{customer.beneficiaryCount || 0}</div>
              <div className={styles.statArrow}>
                <RightOutlined />
              </div>
            </div>
          </div>
          <div className={styles.statCard} onClick={() => onNavigateToSection("history")}>
            <div className={styles.statIcon}>
              <img src={historyIcon} alt="歷史訂單" />
            </div>
            <div className={styles.statContent}>
              <div className={styles.statLabel}>歷史訂單</div>
            </div>
            <div className={styles.statRight}>
              <div className={styles.statValue}>{customer.orderCount || 0}</div>
              <div className={styles.statArrow}>
                <RightOutlined />
              </div>
            </div>
          </div>
        </div>
      </CustomCard>
    </div>
  );
}
